import { b64toBlob, getUsersBrowserData } from '~/utils/helpers'

interface ChangeEntry {
  year: number
  avgMilkAmount: number
  yearlyConcentratedFeedAmountPerCow: number
}

export interface ClimaRegistrationData {
  cowsNumber?: number
  avgMilkAmount?: number
  yearlyConcentratedFeedAmountPerCow?: number
  changesInLastThreeYears?: number
  changes?: ChangeEntry[]
  farmerId?: string
  selfCalculatedFeedingPlan?: number
  consultantLastName?: string
  consultantFirstName?: string
  consultantPhoneNumber?: string
  consultantEmail?: string
  consultantCompanyName?: string
  feedingPlanFile?: {
    data: string
    name: string
  }
}

export function createRegistrationFormData(data: ClimaRegistrationData, firstRegistration = false): FormData {
  const formData = new FormData()
  const browserData = getUsersBrowserData()

  Object.keys(browserData).forEach((key) => {
    //@ts-ignore
    formData.append(key, browserData[key as keyof typeof browserData])
  })

  if (firstRegistration) {
    formData.append('farmerId', data.farmerId?.toString() || '')
  }
  formData.append('cowsNumber', data.cowsNumber?.toString() || '')
  formData.append('avgMilkAmount', data.avgMilkAmount?.toString() || '')
  formData.append('yearlyConcentratedFeedAmountPerCow', data.yearlyConcentratedFeedAmountPerCow?.toString() || '')
  formData.append('changesInLastThreeYears', data.changesInLastThreeYears?.toString() || '')
  formData.append('selfCalculatedFeedingPlan', data.selfCalculatedFeedingPlan?.toString() || '')

  if (data.changesInLastThreeYears === 1 && data.changes) {
    data.changes.forEach((item, index) => {
      //@ts-ignore
      Object.entries(item).forEach(([key, value]) => {
        formData.append(`changes[${index}][${key}]`, value);
      });
    });
  }

  if (data.selfCalculatedFeedingPlan === 0) {
    formData.append('consultantLastName', data.consultantLastName || '')
    formData.append('consultantFirstName', data.consultantFirstName || '')
    formData.append('consultantPhoneNumber', data.consultantPhoneNumber || '')
    formData.append('consultantEmail', data.consultantEmail || '')
    formData.append('consultantCompanyName', data.consultantCompanyName || '')
  }

  if (data.feedingPlanFile?.data?.startsWith('data:')) {
    const mimeType = data.feedingPlanFile.data.split(';')[0].split('data:')[1]
    const blob = b64toBlob(data.feedingPlanFile.data, mimeType)
    formData.append('feedingPlanFile', blob, data.feedingPlanFile.name)
  }
  return formData
}

export function fixDataUrlMimeType(dataUrl: string, fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase()
  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp'
  }
  const mime = (ext && mimeMap[ext]) || 'application/octet-stream'
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
  return `data:${mime};base64,${base64}`
}

export const getFileIcon = (fileName?: string): string => {
  if (!fileName) return 'file alternate outline'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf':
      return 'file pdf outline'
    case 'xlsx':
    case 'xls':
      return 'file excel outline'
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
      return 'file image outline'
    default:
      return 'file alternate outline'
  }
}

export const getFileTypeLabel = (fileName?: string): string => {
  if (!fileName) return 'Document'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf':
      return 'PDF'
    case 'xlsx':
    case 'xls':
      return 'Excel'
    case 'jpg':
    case 'jpeg':
      return 'JPEG'
    case 'png':
      return 'PNG'
    case 'gif':
      return 'GIF'
    case 'bmp':
      return 'BMP'
    default:
      return 'Document'
  }
}
